/**
 * Deal outcome control (2026-08-05 ROI pack) — the attribution write.
 *
 * A small won/lost/pending selector for the trailer page. Every number
 * on the Impact report's outcomes card flows from this (or from CRM
 * sync, which has the same write target and wins when connected).
 *
 * V1 note: the control receives the current status via prop when the
 * caller has it and treats it as optimistic local state after a save —
 * it deliberately does NOT do its own read round-trip.
 */

import { useState } from 'react';

const API_BASE = process.env.REACT_APP_API_URL || 'https://trailercst-backend-production.up.railway.app';

const OPTIONS = [
  { id: 'pending', label: 'Pending decision' },
  { id: 'won', label: 'Closed won' },
  { id: 'lost', label: 'Closed lost' },
];

export default function OutcomeControl({ authToken, jobId, initialStatus = null, initialValueCents = null, onSaved }) {
  const [status, setStatus] = useState(initialStatus);
  const [valueDollars, setValueDollars] = useState(initialValueCents != null ? String(initialValueCents / 100) : '');
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [error, setError] = useState(false);

  async function save(nextStatus, nextValueDollars) {
    setSaving(true);
    setError(false);
    try {
      const cents = nextValueDollars !== '' && !Number.isNaN(Number(nextValueDollars))
        ? Math.round(Number(nextValueDollars) * 100)
        : null;
      const r = await fetch(`${API_BASE}/api/demos/${jobId}/outcome`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${authToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ dealStatus: nextStatus, dealValueCents: cents }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 1800);
      if (onSaved) onSaved({ dealStatus: nextStatus, dealValueCents: cents });
    } catch (_) {
      setError(true);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
      padding: '10px 12px', border: '1px solid var(--room-border, #e2e8f0)', borderRadius: 12,
      background: '#fafbfc', marginTop: 12,
    }}>
      <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--room-text-body, #334155)' }}>Deal outcome</span>
      <select
        value={status || ''}
        disabled={saving}
        onChange={(e) => {
          const v = e.target.value || null;
          setStatus(v);
          save(v, valueDollars);
        }}
        style={{
          padding: '6px 10px', borderRadius: 8, border: '1px solid var(--room-border, #e2e8f0)',
          background: 'var(--room-card, #fff)', fontSize: 12, fontFamily: 'inherit', color: 'var(--room-text-body, #334155)', cursor: 'pointer',
        }}
      >
        <option value="">Not marked</option>
        {OPTIONS.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
      </select>
      <input
        type="number"
        min="0"
        placeholder="Deal value ($)"
        value={valueDollars}
        disabled={saving}
        onChange={(e) => setValueDollars(e.target.value)}
        onBlur={() => { if (status) save(status, valueDollars); }}
        style={{
          width: 110, padding: '6px 10px', borderRadius: 8, border: '1px solid var(--room-border, #e2e8f0)',
          fontSize: 12, fontFamily: 'inherit', color: 'var(--room-text-body, #334155)',
        }}
      />
      {savedFlash && <span style={{ fontSize: 11, color: '#16a34a', fontWeight: 700 }}>Saved</span>}
      {error && <span style={{ fontSize: 11, color: '#dc2626' }}>Couldn't save — retry</span>}
      <span style={{ fontSize: 11, color: 'var(--room-text-subtle, #94a3b8)', flexBasis: '100%' }}>
        Feeds your win-rate on the Impact report. CRM-linked deals sync automatically instead.
      </span>
    </div>
  );
}
